// SPDX-License-Identifier: Elastic-2.0
/**
 * useVoiceSettings — TanStack Query hooks for per-account voice settings
 * (F-225).
 *
 * `useVoiceSettings()` reads `GET /voice/settings`; `useSaveVoiceSettings()`
 * writes via `PUT /voice/settings` and invalidates the query on success so
 * the panel re-renders with the persisted values.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  getVoiceSettings,
  putVoiceSettings,
  type VoiceSettings,
} from "./api";

/** Shared query key — exported so tests + invalidation agree. */
export const VOICE_SETTINGS_QUERY_KEY = ["voice", "settings"] as const;

/**
 * Read the caller's voice settings.
 *
 * Voice is a paid feature (ADR-002): the gateway 402s callers without the
 * `voice_control` entitlement. Pass `enabled: false` for Free tiers so the
 * query never fires (and never surfaces a 402) — the section renders an
 * upgrade prompt instead.
 */
export function useVoiceSettings(
  options: { enabled?: boolean } = {},
): UseQueryResult<VoiceSettings> {
  return useQuery({
    queryKey: VOICE_SETTINGS_QUERY_KEY,
    queryFn: () => getVoiceSettings(),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useSaveVoiceSettings(): UseMutationResult<
  VoiceSettings,
  Error,
  VoiceSettings
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: VoiceSettings) => putVoiceSettings(settings),
    onSuccess: (saved) => {
      // Seed the cache with the server's echo so the form is consistent
      // without waiting for the refetch.
      queryClient.setQueryData(VOICE_SETTINGS_QUERY_KEY, saved);
      void queryClient.invalidateQueries({
        queryKey: VOICE_SETTINGS_QUERY_KEY,
      });
    },
  });
}
